import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Fade,
  Skeleton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Assessment,
  PlayArrow,
  CheckCircle,
  Schedule,
  AccessTime,
  Quiz,
  CheckCircleOutline,
  Update,
  AllInclusive,
  PendingActions,
} from "@mui/icons-material";
import {
  StyledTabsV2,
  StyledTabV2,
  TabContainerV2,
} from "../../Common/UI/TabPanel";
import { errorToastMessage } from "../../../utils/toast";
import http from "../../../utils/http";
import { AxiosResponse } from "axios";
import { theme } from "../../../theme";

const statusLabelMap: any = {
  pending: "Pending",
  not_started: "Not Started",
  completed: "Completed",
};

const statusTextColorMap: any = {
  pending: "#d97706",
  not_started: "#64748b",
  completed: "#059669",
};

const statusBgColorMap: any = {
  pending: "#fef3c7",
  not_started: "#f1f5f9",
  completed: "#d1fae5",
};

const statusIconMap: any = {
  pending: <Schedule />,
  not_started: <PlayArrow />,
  completed: <CheckCircle />,
};

const CoreSurvey = () => {
  const [loading, setLoading] = useState(true);
  const [surveyPackages, setSurveyPackages] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const primaryColor = theme.palette.primary.main;

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const studyId = process.env.REACT_APP_REGISTRY_STUDY_ID;
        const res: AxiosResponse = await http.get(
          `/participant-survey/list?studyId=${studyId}`
        );
        const formattedData = res.data?.data?.map((item: any) => ({
          id: item?.id,
          surveyPackageId: item?.package?.id,
          name: item?.package?.name || "",
          status:
            item?.status === "sent"
              ? item?.totalForms > 0
                ? "pending"
                : "not_started"
              : item?.status,
        }));
        setSurveyPackages(formattedData || []);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };
    fetchData();
  }, [setLoading]);

  const navigateToSurvey = (surveyId: string) => {
    const studyId = process.env.REACT_APP_REGISTRY_STUDY_ID;
    navigate(`/participant/app/surveys/${studyId}/package/${surveyId}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "#059669";
      case "pending":
        return "#d97706";
      default:
        return "#64748b";
    }
  };

  const getStatusIcon = (status: string) => {
    return statusIconMap[status] || <PlayArrow />;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const filteredSurveys = useMemo(() => {
    switch (activeTab) {
      case 0: // All Surveys
        return surveyPackages;
      case 1: // Not Started
        return surveyPackages.filter(
          (survey) => survey.status === "not_started"
        );
      case 2: // In Progress
        return surveyPackages.filter((survey) => survey.status === "pending");
      case 3: // Completed
        return surveyPackages.filter((survey) => survey.status === "completed");
      default:
        return surveyPackages;
    }
  }, [activeTab, surveyPackages]);

  const getTabCounts = () => {
    return {
      all: surveyPackages.length,
      notStarted: surveyPackages.filter(
        (survey) => survey.status === "not_started"
      ).length,
      inProgress: surveyPackages.filter((survey) => survey.status === "pending")
        .length,
      completed: surveyPackages.filter(
        (survey) => survey.status === "completed"
      ).length,
    };
  };

  return (
    <Box
      sx={{
        width: "100%",
        p: { xs: 2, sm: 3, md: 4 },
        minHeight: "100vh",
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            mb: 1,
            fontSize: { xs: "20px", sm: "22px", md: "24px" },
          }}
        >
          Core Registry Surveys
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#475569",
            fontSize: { xs: "14px", sm: "16px" },
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          Complete your core registry surveys to contribute to important medical
          research
        </Typography>

        {/* Modern Tabs */}
        <TabContainerV2>
          <StyledTabsV2 value={activeTab} onChange={handleTabChange}>
            <StyledTabV2
              label="All Surveys"
              icon={<AllInclusive sx={{ fontSize: "18px" }} />}
              count={getTabCounts().all}
              countColor="primary.main"
              countBgColor="secondary.main"
            />
            <StyledTabV2
              label="Not Started"
              icon={<PlayArrow sx={{ fontSize: "18px" }} />}
              count={getTabCounts().notStarted}
              countColor="#64748b"
              countBgColor="rgba(100, 116, 139, 0.1)"
            />
            <StyledTabV2
              label="In Progress"
              icon={<PendingActions sx={{ fontSize: "18px" }} />}
              count={getTabCounts().inProgress}
              countColor="#d97706"
              countBgColor="rgba(217, 119, 6, 0.1)"
            />
            <StyledTabV2
              label="Completed"
              icon={<CheckCircle sx={{ fontSize: "18px" }} />}
              count={getTabCounts().completed}
              countColor="#059669"
              countBgColor="rgba(5, 150, 105, 0.1)"
            />
          </StyledTabsV2>
        </TabContainerV2>
      </Box>

      {/* Loading State */}
      {loading && (
        <Fade in={loading}>
          <Stack spacing={2.5}>
            {[1, 2, 3].map((index) => (
              <Card
                key={index}
                sx={{
                  borderRadius: "16px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  border: "1px solid rgba(226, 232, 240, 0.6)",
                  background: "rgba(255, 255, 255, 0.9)",
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack spacing={2}>
                    {/* Header Row Skeleton */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 1,
                          }}
                        >
                          <Skeleton
                            variant="rectangular"
                            width={48}
                            height={48}
                            sx={{ borderRadius: "12px" }}
                          />
                          <Skeleton
                            variant="text"
                            width="60%"
                            height={24}
                            sx={{ borderRadius: "4px" }}
                          />
                        </Box>
                        <Skeleton
                          variant="text"
                          width="80%"
                          height={16}
                          sx={{ borderRadius: "4px", mb: 1 }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            alignItems: "center",
                          }}
                        >
                          <Skeleton
                            variant="text"
                            width={80}
                            height={16}
                            sx={{ borderRadius: "4px" }}
                          />
                          <Skeleton
                            variant="text"
                            width={100}
                            height={16}
                            sx={{ borderRadius: "4px" }}
                          />
                        </Box>
                      </Box>
                      <Skeleton
                        variant="rectangular"
                        width={80}
                        height={32}
                        sx={{ borderRadius: "20px" }}
                      />
                    </Box>

                    {/* Bottom Section Skeleton */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pt: 1,
                      }}
                    >
                      <Skeleton
                        variant="text"
                        width={120}
                        height={14}
                        sx={{ borderRadius: "4px" }}
                      />
                      <Skeleton
                        variant="rectangular"
                        width={100}
                        height={36}
                        sx={{ borderRadius: "12px" }}
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Fade>
      )}

      {/* Survey Cards */}
      {!loading && (
        <Fade in={!loading} timeout={600}>
          <Box>
            {filteredSurveys.length > 0 ? (
              <Stack
                spacing={2.5}
                sx={{
                  "& > *": {
                    width: "100%",
                  },
                }}
              >
                {filteredSurveys?.map((item: any, index: number) => (
                  <Card
                    key={item.id}
                    sx={{
                      borderRadius: "16px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                      border: "1px solid rgba(226, 232, 240, 0.6)",
                      background: "rgba(255, 255, 255, 0.9)",
                      backdropFilter: "blur(10px)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack spacing={2}>
                        {/* Header Row */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 2,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  p: 1.5,
                                  borderRadius: "12px",
                                  background: `linear-gradient(135deg, ${primaryColor} 0%, #2563eb 100%)`,
                                  color: "white",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Assessment sx={{ fontSize: "20px" }} />
                              </Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600,
                                  color: "#0f172a",
                                  fontSize: { xs: "16px", sm: "18px" },
                                }}
                              >
                                {item?.name || "Untitled Survey"}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#64748b",
                                fontSize: "14px",
                                lineHeight: 1.5,
                                mb: 1,
                              }}
                            >
                              {item?.description ||
                                "Core registry survey for comprehensive data collection"}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                flexWrap: "wrap",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#64748b",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <AccessTime
                                  sx={{ fontSize: "14px", color: "#0ea5e9" }}
                                />
                                {item?.estimatedTime || "15-20 min"}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#64748b",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Quiz
                                  sx={{ fontSize: "14px", color: "#8b5cf6" }}
                                />
                                {item?.totalQuestions || "30"} questions
                              </Typography>
                              {item?.status === "completed" &&
                                item?.completedDate && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#059669",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <CheckCircleOutline
                                      sx={{
                                        fontSize: "14px",
                                        color: "#10b981",
                                      }}
                                    />
                                    Completed{" "}
                                    {new Date(
                                      item.completedDate
                                    ).toLocaleDateString()}
                                  </Typography>
                                )}
                            </Box>
                          </Box>
                          <Chip
                            icon={getStatusIcon(item?.status)}
                            label={statusLabelMap[item?.status] || "Unknown"}
                            sx={{
                              borderRadius: "20px",
                              px: 2,
                              py: 0.5,
                              fontSize: "12px",
                              fontWeight: 600,
                              color: statusTextColorMap[item?.status],
                              bgcolor: statusBgColorMap[item?.status],
                              border: `1px solid ${getStatusColor(
                                item?.status
                              )}20`,
                              "& .MuiChip-icon": {
                                fontSize: "16px",
                                color: statusTextColorMap[item?.status],
                              },
                            }}
                          />
                        </Box>

                        {/* Bottom Section */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            pt: 1,
                          }}
                        >
                          {/* Left side - Last Updated */}
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#94a3b8",
                              fontSize: "11px",
                              fontWeight: 500,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Update sx={{ fontSize: "12px" }} />
                            Updated{" "}
                            {new Date(
                              item?.lastUpdated || new Date()
                            ).toLocaleDateString()}
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={() => navigateToSurvey(item.id)}
                            color={
                              item?.status === "completed"
                                ? "success"
                                : "primary"
                            }
                          >
                            {item?.status === "completed"
                              ? "View Results"
                              : "Start Survey"}
                          </Button>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              /* Empty State for Tab */
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "400px",
                  textAlign: "center",
                  p: 4,
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                    mb: 3,
                  }}
                >
                  {activeTab === 0 && (
                    <Assessment sx={{ fontSize: "48px", color: "#1d4ed8" }} />
                  )}
                  {activeTab === 1 && (
                    <PlayArrow sx={{ fontSize: "48px", color: "#64748b" }} />
                  )}
                  {activeTab === 2 && (
                    <PendingActions
                      sx={{ fontSize: "48px", color: "#d97706" }}
                    />
                  )}
                  {activeTab === 3 && (
                    <CheckCircle sx={{ fontSize: "48px", color: "#059669" }} />
                  )}
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: "#64748b",
                    mb: 2,
                    fontSize: { xs: "20px", sm: "24px" },
                  }}
                >
                  {activeTab === 0 && "No Surveys Available"}
                  {activeTab === 1 && "No Surveys Not Started"}
                  {activeTab === 2 && "No Surveys In Progress"}
                  {activeTab === 3 && "No Surveys Completed"}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#64748b",
                    maxWidth: "400px",
                    lineHeight: 1.6,
                  }}
                >
                  {activeTab === 0 &&
                    "There are currently no core registry surveys available for your study. Check back later for updates."}
                  {activeTab === 1 &&
                    "You have no surveys that haven't been started yet. All available surveys are either in progress or completed."}
                  {activeTab === 2 &&
                    "You have no surveys currently in progress. Start a survey to see it here."}
                  {activeTab === 3 &&
                    "You haven't completed any surveys yet. Complete your first survey to see it here."}
                </Typography>
              </Box>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default CoreSurvey;
